import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  TruckIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  DocumentArrowDownIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  UserIcon,
  BuildingOfficeIcon,
  XMarkIcon,
  CurrencyDollarIcon,
  CheckBadgeIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';
import toast from 'react-hot-toast';
import OrderDetails from '../../components/admin/OrderDetails';
import { LoadingSpinner, EmptyState, AccessibleButton } from '../../components/ui';

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetailsModal, setShowOrderDetailsModal] = useState(false);

  // Helper function to calculate the discount applied to order totals.
  // Item-level discounts are already baked into unit_price (unit_price is the
  // final discounted price), so only the order-level discount affects totals.
  const calculateTotalDiscount = (order) => {
    return Math.abs(parseFloat(order.discount_amount || 0));
  };

  // Helper function to calculate correct order total with VAT
  // Excludes tester and bonus items, adds 18% VAT
  const calculateOrderTotal = (order) => {
    // If no items, fall back to stored total
    if (!order.items || order.items.length === 0) {
      return parseFloat(order.total_amount || 0);
    }
    
    // Calculate subtotal from items, excluding tester/bonus
    const subtotal = order.items.reduce((sum, item) => {
      const isTesterOrBonus = item.is_tester || item.is_bonus || 
        (item.notes && (item.notes.includes('טסטר') || item.notes.includes('בונוס')));
      if (isTesterOrBonus) {
        return sum;
      }
      const price = parseFloat(item.unit_price || item.price || 0);
      return sum + (price * (item.quantity || 1));
    }, 0);
    
    // Matches server calculation: VAT (18%) on subtotal after order-level discount
    const discount = calculateTotalDiscount(order);
    const subtotalAfterDiscount = Math.max(0, subtotal - discount);
    const shipping = parseFloat(order.shipping_amount || 0);

    return subtotalAfterDiscount * 1.18 + shipping;
  };

  // Filters and search
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    payment_status: '',
    customer_type: '',
    date_from: '',
    date_to: '',
    min_amount: '',
    max_amount: '',
    sortBy: 'created_at',
    sortOrder: 'desc'
  });

  // Pagination
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20
  });

  // Global status counts for statistics (not affected by filters)
  const [statusCounts, setStatusCounts] = useState({
    pending: 0,
    processing: 0,
    shipped: 0,
    delivered: 0,
    cancelled: 0,
    refunded: 0,
    total: 0
  });

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    // Load filters from URL
    const urlFilters = {
      search: searchParams.get('search') || '',
      status: searchParams.get('status') || '',
      payment_status: searchParams.get('payment_status') || '',
      customer_type: searchParams.get('customer_type') || '',
      date_from: searchParams.get('date_from') || '',
      date_to: searchParams.get('date_to') || '',
      min_amount: searchParams.get('min_amount') || '',
      max_amount: searchParams.get('max_amount') || '',
      sortBy: searchParams.get('sort') || 'created_at',
      sortOrder: searchParams.get('order') || 'desc'
    };
    setFilters(urlFilters);
    
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Auto-open order details if autoOpen parameter is present
  useEffect(() => {
    const autoOpenId = searchParams.get('autoOpen');
    if (autoOpenId && orders.length > 0) {
      const orderToOpen = orders.find(o => o.id === autoOpenId);
      if (orderToOpen) {
        setSelectedOrder(orderToOpen);
        setShowOrderDetailsModal(true);
        // Remove the autoOpen parameter from URL to prevent re-opening on refresh
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('autoOpen');
        setSearchParams(newParams, { replace: true });
      }
    }
  }, [orders, searchParams, setSearchParams]);

  const fetchOrders = async (customFilters = null, page = null) => {
    try {
      setLoading(true);
      const activeFilters = customFilters || filters;
      const currentPage = page || pagination.currentPage;
      
      // Build query params
      const params = new URLSearchParams();
      if (activeFilters.search) params.append('search', activeFilters.search);
      if (activeFilters.status) params.append('status', activeFilters.status);
      if (activeFilters.payment_status) params.append('payment_status', activeFilters.payment_status);
      if (activeFilters.customer_type) params.append('order_type', activeFilters.customer_type);
      if (activeFilters.date_from) params.append('date_from', activeFilters.date_from);
      if (activeFilters.date_to) params.append('date_to', activeFilters.date_to);
      if (activeFilters.min_amount) params.append('min_amount', activeFilters.min_amount);
      if (activeFilters.max_amount) params.append('max_amount', activeFilters.max_amount);
      params.append('sort', activeFilters.sortBy);
      params.append('order', activeFilters.sortOrder);
      params.append('page', currentPage);
      params.append('limit', pagination.itemsPerPage);

      const response = await axios.get(`/api/orders?${params.toString()}`);
      // Note: /api/orders endpoint is filtered by admin auth to show all orders

      setOrders(response.data.orders || []);
      setPagination(response.data.pagination || pagination);
      
      // Update global status counts if returned from API
      if (response.data.statusCounts) {
        setStatusCounts(response.data.statusCounts);
      }

    } catch (error) {
      console.error('Error fetching orders:', error);
      // Show empty state instead of mock data
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    updateFilters({ ...filters, search: e.target.search.value });
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;
    
    setPagination(prev => ({
      ...prev,
      currentPage: newPage
    }));
    
    fetchOrders(filters, newPage);
  };

  const updateFilters = (newFilters) => {
    setFilters(newFilters);
    
    // Update URL
    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([key, value]) => {
      if (value && value !== '') {
        if (key === 'sortBy') {
          params.append('sort', value);
        } else if (key === 'sortOrder') {
          params.append('order', value);
        } else {
          params.append(key.replace(/([A-Z])/g, '_$1').toLowerCase(), value);
        }
      }
    });
    
    setSearchParams(params);
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      await axios.put(`/api/orders/${orderId}/status`, {
        status: newStatus
      });
      
      // Update local state
      setOrders(prevOrders =>
        prevOrders.map(order =>
          order.id === orderId ? { ...order, status: newStatus } : order
        )
      );
      
      toast.success('סטטוס ההזמנה עודכן בהצלחה');
    } catch (error) {
      toast.error(error.response?.data?.message || 'שגיאה בעדכון סטטוס ההזמנה');
    }
  };

  const handlePaymentStatusUpdate = async (orderId, newPaymentStatus) => {
    try {
      // Find the order to get payment method and company info
      const order = orders.find(o => o.id === orderId);
      
      await axios.put(`/api/orders/${orderId}/payment`, {
        payment_status: newPaymentStatus
      });
      
      // If marking as paid and payment method is credit, confirm the purchase
      if (newPaymentStatus === 'paid' && order?.payment_method === 'company_credit' && order?.company_id) {
        try {
          await axios.post(`/api/credit/company/${order.company_id}/confirm-purchase`, {
            order_id: orderId,
            description: `הזמנה #${order.order_number} - אושרה לתשלום`
          });
        } catch (creditError) {
          console.error('Error confirming credit purchase:', creditError);
          // Don't fail the payment status update if credit confirmation fails
          alert('התשלום עודכן אך יש בעיה באישור האשראי. אנא בדוק את מסגרת האשראי.');
        }
      }
      
      // Update local state
      setOrders(prevOrders =>
        prevOrders.map(order =>
          order.id === orderId ? { ...order, payment_status: newPaymentStatus } : order
        )
      );
      
      toast.success('סטטוס התשלום עודכן בהצלחה');
    } catch (error) {
      toast.error(error.response?.data?.message || 'שגיאה בעדכון סטטוס התשלום');
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { text: 'בהמתנה', className: 'bg-yellow-100 text-yellow-800 border border-yellow-200' },
      processing: { text: 'בעיבוד', className: 'bg-blue-100 text-blue-800 border border-blue-200' },
      shipped: { text: 'נשלח', className: 'bg-purple-100 text-purple-800 border border-purple-200' },
      delivered: { text: 'נמסר', className: 'bg-green-100 text-green-800 border border-green-200' },
      cancelled: { text: 'בוטל', className: 'bg-red-100 text-red-800 border border-red-200' },
      refunded: { text: 'הוחזר', className: 'bg-gray-100 text-gray-800 border border-gray-200' }
    };
    const badge = statusMap[status] || { text: status, className: 'bg-gray-100 text-gray-800 border border-gray-200' };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${badge.className}`}>
        {badge.text}
      </span>
    );
  };

  const getPaymentStatusBadge = (paymentStatus) => {
    const statusMap = {
      pending: { text: 'בהמתנה', className: 'bg-yellow-100 text-yellow-800 border border-yellow-200' },
      paid: { text: 'שולם', className: 'bg-green-100 text-green-800 border border-green-200' },
      failed: { text: 'נכשל', className: 'bg-red-100 text-red-800 border border-red-200' },
      refunded: { text: 'הוחזר', className: 'bg-orange-100 text-orange-800 border border-orange-200' },
      partially_refunded: { text: 'הוחזר חלקית', className: 'bg-orange-100 text-orange-800 border border-orange-200' }
    };
    const badge = statusMap[paymentStatus] || { text: paymentStatus, className: 'bg-gray-100 text-gray-800 border border-gray-200' };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${badge.className}`}>
        {badge.text}
      </span>
    );
  };

  // Get row background color based on order status
  const getRowStatusColor = (status) => {
    const colorMap = {
      pending: 'bg-yellow-50 hover:bg-yellow-100 border-r-4 border-r-yellow-400',
      processing: 'bg-blue-50 hover:bg-blue-100 border-r-4 border-r-blue-400',
      shipped: 'bg-purple-50 hover:bg-purple-100 border-r-4 border-r-purple-400',
      delivered: 'bg-green-50 hover:bg-green-100 border-r-4 border-r-green-400',
      cancelled: 'bg-red-50 hover:bg-red-100 border-r-4 border-r-red-400',
      refunded: 'bg-gray-50 hover:bg-gray-100 border-r-4 border-r-gray-400'
    };
    return colorMap[status] || 'hover:bg-gray-50';
  };

  const clearFilters = () => {
    const clearedFilters = {
      search: '',
      status: '',
      payment_status: '',
      customer_type: '',
      date_from: '',
      date_to: '',
      min_amount: '',
      max_amount: '',
      sortBy: 'created_at',
      sortOrder: 'desc'
    };
    updateFilters(clearedFilters);
  };

  const hasActiveFilters = () => {
    return filters.status || filters.payment_status || filters.customer_type || 
           filters.date_from || filters.date_to || filters.min_amount || filters.max_amount || filters.search;
  };

  const handleOrderUpdated = (updatedOrder) => {
    // Update the order in the list
    setOrders(prevOrders =>
      prevOrders.map(order =>
        order.id === updatedOrder.id ? { ...order, ...updatedOrder } : order
      )
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) {
      return 'תאריך לא זמין';
    }
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'תאריך לא תקין';
      }
      
      return date.toLocaleDateString('he-IL', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'שגיאה בתאריך';
    }
  };

  const exportOrdersToCSV = () => {
    if (!orders || orders.length === 0) {
      toast.error('אין הזמנות לייצוא');
      return;
    }

    const statusLabels = {
      pending: 'בהמתנה', processing: 'בעיבוד', shipped: 'נשלח',
      delivered: 'נמסר', cancelled: 'בוטל', refunded: 'הוחזר'
    };
    const paymentLabels = {
      pending: 'בהמתנה', paid: 'שולם', failed: 'נכשל',
      refunded: 'הוחזר', partially_refunded: 'הוחזר חלקית'
    };

    const rows = [['מספר הזמנה', 'תאריך', 'לקוח', 'חברה', 'סוג', 'סטטוס', 'סטטוס תשלום', 'סה"כ', 'פריטים']];

    orders.forEach(order => {
      rows.push([
        order.order_number || '',
        new Date(order.created_at).toLocaleDateString('he-IL'),
        order.user ? `${order.user.first_name || ''} ${order.user.last_name || ''}`.trim() : '',
        order.company?.name || '',
        order.order_type === 'b2b' ? 'B2B' : 'B2C',
        statusLabels[order.status] || order.status,
        paymentLabels[order.payment_status] || order.payment_status,
        calculateOrderTotal(order),
        order.items?.length || 0
      ]);
    });

    const csvContent = '\uFEFF' + rows.map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const dateStr = new Date().toISOString().slice(0, 10);
    link.href = URL.createObjectURL(blob);
    link.download = `orders_report_${dateStr}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
    toast.success('הדוח יוצא בהצלחה');
  };

  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <LoadingSpinner size="lg" color="primary" text="טוען הזמנות..." />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 p-2 sm:p-0">
      {/* Header */}
      <div className="flex flex-col space-y-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">ניהול הזמנות</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">עיבוד הזמנות, מעקב משלוחים ותשלומים</p>
        </div>
        <div className="flex gap-2">
          <AccessibleButton
            variant="outline"
            size="md"
            icon={DocumentArrowDownIcon}
            iconPosition="left"
            ariaLabel="יצוא דוח הזמנות"
            onClick={exportOrdersToCSV}
          >
            <span className="hidden sm:inline">יצוא דוח</span>
            <span className="sm:hidden">יצוא</span>
          </AccessibleButton>
        </div>
      </div>

      {/* Quick Stats - Using global counts (not affected by current page or filters) */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div 
          className={`bg-gradient-to-br from-yellow-50 to-yellow-100 p-4 sm:p-6 rounded-xl shadow-lg border border-yellow-200 hover:shadow-xl transition-shadow cursor-pointer ${filters.status === 'pending' ? 'ring-2 ring-yellow-500' : ''}`}
          onClick={() => updateFilters({ ...filters, status: filters.status === 'pending' ? '' : 'pending' })}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-semibold text-yellow-900 mb-1">בהמתנה</p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900">
                {statusCounts.pending}
              </p>
            </div>
            <div className="p-3 bg-yellow-500 rounded-xl shadow-md">
              <ClockIcon className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
            </div>
          </div>
        </div>
        
        <div 
          className={`bg-gradient-to-br from-blue-50 to-blue-100 p-4 sm:p-6 rounded-xl shadow-lg border border-blue-200 hover:shadow-xl transition-shadow cursor-pointer ${filters.status === 'processing' ? 'ring-2 ring-blue-500' : ''}`}
          onClick={() => updateFilters({ ...filters, status: filters.status === 'processing' ? '' : 'processing' })}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-semibold text-blue-900 mb-1">בעיבוד</p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900">
                {statusCounts.processing}
              </p>
            </div>
            <div className="p-3 bg-blue-500 rounded-xl shadow-md">
              <CheckCircleIcon className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
            </div>
          </div>
        </div>
        
        <div 
          className={`bg-gradient-to-br from-green-50 to-green-100 p-4 sm:p-6 rounded-xl shadow-lg border border-green-200 hover:shadow-xl transition-shadow cursor-pointer ${filters.status === 'shipped' ? 'ring-2 ring-green-500' : ''}`}
          onClick={() => updateFilters({ ...filters, status: filters.status === 'shipped' ? '' : 'shipped' })}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-semibold text-green-900 mb-1">נשלח</p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900">
                {statusCounts.shipped}
              </p>
            </div>
            <div className="p-3 bg-green-500 rounded-xl shadow-md">
              <TruckIcon className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
            </div>
          </div>
        </div>
        
        <div 
          className={`bg-gradient-to-br from-emerald-50 to-emerald-100 p-4 sm:p-6 rounded-xl shadow-lg border border-emerald-200 hover:shadow-xl transition-shadow cursor-pointer ${filters.status === 'delivered' ? 'ring-2 ring-emerald-500' : ''}`}
          onClick={() => updateFilters({ ...filters, status: filters.status === 'delivered' ? '' : 'delivered' })}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-semibold text-emerald-900 mb-1">נמסר</p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900">
                {statusCounts.delivered}
              </p>
            </div>
            <div className="p-3 bg-emerald-500 rounded-xl shadow-md">
              <CheckBadgeIcon className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
            </div>
          </div>
        </div>
        
        <div 
          className={`bg-gradient-to-br from-red-50 to-red-100 p-4 sm:p-6 rounded-xl shadow-lg border border-red-200 hover:shadow-xl transition-shadow cursor-pointer ${filters.status === 'cancelled' ? 'ring-2 ring-red-500' : ''}`}
          onClick={() => updateFilters({ ...filters, status: filters.status === 'cancelled' ? '' : 'cancelled' })}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-semibold text-red-900 mb-1">בוטל</p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900">
                {statusCounts.cancelled}
              </p>
            </div>
            <div className="p-3 bg-red-500 rounded-xl shadow-md">
              <XCircleIcon className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-4 sm:p-6 rounded-xl shadow-lg border border-gray-100">
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3 items-end">
            <div className="flex-1 w-full">
              <label htmlFor="search-input" className="block text-sm font-medium text-gray-700 mb-2">
                חיפוש הזמנות
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </div>
                <input
                  id="search-input"
                  type="text"
                  name="search"
                  placeholder="חפש לפי מספר הזמנה, לקוח, חברה או שם מוצר..."
                  defaultValue={filters.search}
                  className="block w-full pr-10 pl-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                  aria-label="חיפוש הזמנות"
                />
              </div>
            </div>
            
            <div className="flex gap-2 w-full sm:w-auto">
              <AccessibleButton
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                variant={showFilters ? 'primary' : 'outline'}
                size="md"
                icon={FunnelIcon}
                iconPosition="left"
                fullWidth
                className="sm:w-auto"
                ariaLabel="הצג/הסתר מסננים"
              >
                מסננים
              </AccessibleButton>
              
              <AccessibleButton
                type="submit"
                variant="primary"
                size="md"
                icon={MagnifyingGlassIcon}
                iconPosition="left"
                fullWidth
                className="sm:w-auto"
                ariaLabel="חפש הזמנות"
              >
                חפש
              </AccessibleButton>
            </div>
          </div>

          {hasActiveFilters() && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-gray-600">פילטרים פעילים:</span>
              {filters.status && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-lg text-xs font-medium">
                  סטטוס: {filters.status}
                </span>
              )}
              {filters.payment_status && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-lg text-xs font-medium">
                  תשלום: {filters.payment_status}
                </span>
              )}
              {filters.search && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 rounded-lg text-xs font-medium">
                  חיפוש: {filters.search}
                </span>
              )}
              <AccessibleButton
                type="button"
                onClick={clearFilters}
                variant="ghost"
                size="sm"
                icon={XMarkIcon}
                iconPosition="left"
                ariaLabel="נקה פילטרים"
              >
                נקה הכל
              </AccessibleButton>
            </div>
          )}
        </form>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label htmlFor="status-filter" className="block text-sm font-medium text-gray-700 mb-2">
                  סטטוס הזמנה
                </label>
                <select
                  id="status-filter"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                  value={filters.status}
                  onChange={(e) => updateFilters({ ...filters, status: e.target.value })}
                >
                  <option value="">כל הסטטוסים</option>
                  <option value="pending">בהמתנה</option>
                  <option value="processing">בעיבוד</option>
                  <option value="shipped">נשלח</option>
                  <option value="delivered">נמסר</option>
                  <option value="cancelled">בוטל</option>
                </select>
              </div>

              <div>
                <label htmlFor="payment-status-filter" className="block text-sm font-medium text-gray-700 mb-2">
                  סטטוס תשלום
                </label>
                <select
                  id="payment-status-filter"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                  value={filters.payment_status}
                  onChange={(e) => updateFilters({ ...filters, payment_status: e.target.value })}
                >
                  <option value="">כל הסטטוסים</option>
                  <option value="pending">בהמתנה</option>
                  <option value="paid">שולם</option>
                  <option value="failed">נכשל</option>
                  <option value="refunded">הוחזר</option>
                </select>
              </div>

              <div>
                <label htmlFor="customer-type-filter" className="block text-sm font-medium text-gray-700 mb-2">
                  סוג לקוח
                </label>
                <select
                  id="customer-type-filter"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                  value={filters.customer_type}
                  onChange={(e) => updateFilters({ ...filters, customer_type: e.target.value })}
                >
                  <option value="">כל הלקוחות</option>
                  <option value="b2c">B2C - לקוח פרטי</option>
                  <option value="b2b">B2B - לקוח עסקי</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  מתאריך
                </label>
                <input
                  type="date"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                  value={filters.date_from}
                  onChange={(e) => updateFilters({ ...filters, date_from: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  עד תאריך
                </label>
                <input
                  type="date"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                  value={filters.date_to}
                  onChange={(e) => updateFilters({ ...filters, date_to: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  סכום מינימלי
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <CurrencyDollarIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    placeholder="מ-"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                    value={filters.min_amount}
                    onChange={(e) => updateFilters({ ...filters, min_amount: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  סכום מקסימלי
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <CurrencyDollarIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    placeholder="עד-"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                    value={filters.max_amount}
                    onChange={(e) => updateFilters({ ...filters, max_amount: e.target.value })}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Orders Table */}
      {orders.length === 0 && !loading ? (
        <div className="bg-white shadow-lg rounded-xl border border-gray-100">
          <EmptyState
            type="orders"
            title="אין הזמנות"
            description={hasActiveFilters() ? 'לא נמצאו הזמנות התואמות את הפילטרים שלך' : 'ההזמנות יופיעו כאן כשיתקבלו'}
            actionText={hasActiveFilters() ? 'נקה פילטרים' : undefined}
            onAction={hasActiveFilters() ? clearFilters : undefined}
          />
        </div>
      ) : (
        <div className="bg-white shadow-lg rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                <tr>
                  <th className="px-4 sm:px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    הזמנה
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    לקוח
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    סטטוס הזמנה
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    סטטוס תשלום
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider hidden md:table-cell">
                    תאריך הזמנה
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    סה״כ
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {orders.map((order) => (
                  <tr 
                    key={order.id} 
                    className={`${getRowStatusColor(order.status)} transition-colors cursor-pointer`}
                    onClick={() => {
                      setSelectedOrder(order);
                      setShowOrderDetailsModal(true);
                    }}
                  >
                    <td className="px-4 sm:px-6 py-4">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="text-sm font-semibold text-gray-900">
                            {order.order_number}
                          </div>
                          {order.seller_id && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-gradient-to-r from-purple-100 to-pink-100 text-purple-800 border border-purple-300">
                              👤 סוכן
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {order.items?.length || 0} פריטים
                        </div>
                        {order.seller_id && order.seller && (
                          <div className="text-xs text-purple-600 font-medium mt-1">
                            סוכן: {order.seller.first_name} {order.seller.last_name}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="flex-shrink-0">
                          {order.order_type === 'b2b' ? (
                            <BuildingOfficeIcon className="h-5 w-5 text-blue-500" />
                          ) : (
                            <UserIcon className="h-5 w-5 text-green-500" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          {/* B2B: Show company name first, B2C: Show customer name */}
                          {order.order_type === 'b2b' && order.company ? (
                            <>
                              <div className="text-sm font-medium text-gray-900 truncate">
                                🏢 {order.company.name}
                              </div>
                              {order.user && (
                                <div className="text-xs text-gray-600 truncate">
                                  איש קשר: {order.user.first_name} {order.user.last_name}
                                </div>
                              )}
                              <div className="text-xs text-gray-500 truncate">
                                {order.company.email || order.user?.email || 'אימייל לא זמין'}
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {order.user ? `${order.user.first_name} ${order.user.last_name}` : 'לקוח לא ידוע'}
                              </div>
                              <div className="text-xs text-gray-500 truncate">
                                {order.user?.email || 'אימייל לא זמין'}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-4">
                      <select
                        className="text-xs rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent px-2 py-1 bg-white transition-all"
                        value={order.status}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleStatusUpdate(order.id, e.target.value);
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <option value="pending">בהמתנה</option>
                        <option value="processing">בעיבוד</option>
                        <option value="shipped">נשלח</option>
                        <option value="delivered">נמסר</option>
                        <option value="cancelled">בוטל</option>
                      </select>
                    </td>
                    <td className="px-4 sm:px-6 py-4">
                      <select
                        className="text-xs rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent px-2 py-1 bg-white transition-all"
                        value={order.payment_status}
                        onChange={(e) => {
                          e.stopPropagation();
                          handlePaymentStatusUpdate(order.id, e.target.value);
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <option value="pending">בהמתנה</option>
                        <option value="paid">שולם</option>
                        <option value="failed">נכשל</option>
                        <option value="refunded">הוחזר</option>
                      </select>
                    </td>
                    <td className="px-4 sm:px-6 py-4 hidden md:table-cell text-sm text-gray-900">
                      {formatDate(order.created_at || order.createdAt)}
                    </td>
                    <td className="px-4 sm:px-6 py-4">
                      <div className="text-sm font-semibold text-gray-900">
                        {formatCurrency(calculateOrderTotal(order))}
                      </div>
                      {(() => {
                        const totalDiscount = calculateTotalDiscount(order);
                        return totalDiscount > 0 && (
                          <div className="text-xs text-green-600 font-medium">
                            הנחה: -{formatCurrency(totalDiscount)}
                          </div>
                        );
                      })()}
                      <div className="text-xs text-gray-500 mt-1">
                        {formatDate(order.created_at || order.createdAt)}
                        <span className="md:hidden"> • {order.order_type === 'b2b' ? 'B2B' : 'B2C'}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <AccessibleButton
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage === 1}
                  variant="outline"
                  size="sm"
                  ariaLabel="עמוד קודם"
                >
                  הקודם
                </AccessibleButton>
                <AccessibleButton
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage === pagination.totalPages}
                  variant="outline"
                  size="sm"
                  className="mr-3"
                  ariaLabel="עמוד הבא"
                >
                  הבא
                </AccessibleButton>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    מציג{' '}
                    <span className="font-semibold">
                      {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1}
                    </span>{' '}
                    עד{' '}
                    <span className="font-semibold">
                      {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)}
                    </span>{' '}
                    מתוך{' '}
                    <span className="font-semibold">{pagination.totalItems}</span>{' '}
                    הזמנות
                  </p>
                </div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="עמודים">
                  <AccessibleButton
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={pagination.currentPage === 1}
                    variant="outline"
                    size="sm"
                    className="rounded-r-md"
                    ariaLabel="עמוד קודם"
                  >
                    הקודם
                  </AccessibleButton>
                  <AccessibleButton
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={pagination.currentPage === pagination.totalPages}
                    variant="outline"
                    size="sm"
                    className="rounded-l-md"
                    ariaLabel="עמוד הבא"
                  >
                    הבא
                  </AccessibleButton>
                </nav>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Order Details Modal */}
      <OrderDetails
        isOpen={showOrderDetailsModal}
        onClose={() => {
          setShowOrderDetailsModal(false);
          setSelectedOrder(null);
        }}
        order={selectedOrder}
        onOrderUpdated={handleOrderUpdated}
      />
    </div>
  );
};

export default AdminOrders;
